/**
 * Protokoll — das Audit-Log als Leitstand-Seite (Phase B).
 *
 * Funktionsumfang wie die bewährte Enterprise-Ansicht: Server-Blättern,
 * Filter (Aktion, Bereich, Zeitraum, Volltext — die Suche läuft in der
 * Datenbank, nicht im Browser), aufklappbare Einzelheiten mit Alt/Neu-Werten
 * und Kopieren. Nur SYSTEM und SUPERADMIN dürfen hinein — das Protokoll
 * enthält Alt-Werte aus allen Bereichen.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kennzahl,
  Kopfzelle,
  Notiz,
  Tabelle,
  Zelle,
} from '../components/leitstand';

interface ProtokollEintrag {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  oldValue: string | null;
  newValue: string | null;
  details: string | null;
  ip: string | null;
  userAgent: string | null;
  timestamp: string;
  user: { id: string; displayName: string };
}

const ZEITRAEUME = [
  { schluessel: '24h', label: '24 H', stunden: 24 },
  { schluessel: '7d', label: '7 TAGE', stunden: 168 },
  { schluessel: '30d', label: '30 TAGE', stunden: 720 },
  { schluessel: 'all', label: 'ALLES', stunden: 0 },
] as const;
type Zeitraum = (typeof ZEITRAEUME)[number]['schluessel'];

const AKTIONEN: Array<{ code: string; label: string }> = [
  { code: 'CREATE', label: 'Angelegt' },
  { code: 'UPDATE', label: 'Geändert' },
  { code: 'DELETE', label: 'Gelöscht' },
  { code: 'LOGIN', label: 'Anmeldung' },
  { code: 'LOGOUT', label: 'Abmeldung' },
  { code: 'EXPORT', label: 'Export' },
];

const SEITEN_GROESSEN = [25, 50, 100];

function aktionsLabel(code: string): string {
  return AKTIONEN.find((a) => a.code === code)?.label ?? code;
}

function zeitText(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function alsLesbar(roh: string | null): Array<[string, string]> | string | null {
  if (!roh) return null;
  try {
    const obj = JSON.parse(roh);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      return Object.entries(obj).map(([k, v]) => [
        k,
        typeof v === 'object' ? JSON.stringify(v) : String(v),
      ]);
    }
    return roh;
  } catch {
    return roh;
  }
}

function Umschalter<T extends string>({
  wert,
  optionen,
  onWechsel,
}: {
  wert: T;
  optionen: ReadonlyArray<{ schluessel: T; label: string }>;
  onWechsel: (neu: T) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {optionen.map((o) => (
        <button
          key={o.schluessel}
          onClick={() => onWechsel(o.schluessel)}
          aria-pressed={wert === o.schluessel}
          className={`rounded-[4px] border px-[11px] py-1.5 font-zahl text-xs transition-colors ${
            wert === o.schluessel
              ? 'border-akzent bg-akzent text-akzent-satz'
              : 'border-linie-2 bg-flaeche text-tinte-2 hover:border-linie-stark'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function WertBlock({
  marke,
  roh,
  id,
  copiedId,
  onCopy,
}: {
  marke: string;
  roh: string;
  id: string;
  copiedId: string | null;
  onCopy: (text: string, id: string) => void;
}) {
  const lesbar = alsLesbar(roh);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
          {marke}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCopy(roh, id);
          }}
          className="font-zahl text-[11px] text-tinte-3 underline hover:text-tinte-2"
        >
          {copiedId === id ? 'kopiert' : 'kopieren'}
        </button>
      </div>
      <div className="overflow-x-auto rounded-[4px] border border-linie-2 bg-flaeche p-3 font-zahl text-xs">
        {Array.isArray(lesbar) ? (
          lesbar.map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span className="text-tinte-3">{k}:</span>
              <span className="break-all text-tinte">{v}</span>
            </div>
          ))
        ) : (
          <span className="whitespace-pre-wrap break-all text-tinte">{lesbar}</span>
        )}
      </div>
    </div>
  );
}

export default function AuditBetrieb() {
  const { user } = useAuth();

  const [eintraege, setEintraege] = useState<ProtokollEintrag[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const [aktion, setAktion] = useState('');
  const [bereich, setBereich] = useState('');
  const [suchtext, setSuchtext] = useState('');
  const [suche, setSuche] = useState('');
  const [zeitraum, setZeitraum] = useState<Zeitraum>('7d');

  const [seite, setSeite] = useState(0);
  const [proSeite, setProSeite] = useState(50);

  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Suche entprellen — jede Eingabe soll nicht sofort eine Abfrage auslösen
  useEffect(() => {
    const t = setTimeout(() => {
      setSuche(suchtext);
      setSeite(0);
    }, 400);
    return () => clearTimeout(t);
  }, [suchtext]);

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(proSeite));
      params.set('offset', String(seite * proSeite));
      if (aktion) params.set('action', aktion);
      if (bereich) params.set('entity', bereich);
      if (suche) params.set('search', suche);
      const stunden = ZEITRAEUME.find((z) => z.schluessel === zeitraum)?.stunden ?? 0;
      if (stunden > 0) {
        params.set('from', new Date(Date.now() - stunden * 3600000).toISOString());
        params.set('to', new Date().toISOString());
      }
      const res = await fetch(`/api/admin/audit?${params}`, { credentials: 'include' });
      if (res.status === 403) throw new Error('Keine Berechtigung für das Protokoll');
      if (!res.ok) throw new Error('Das Protokoll konnte nicht geladen werden');
      const body = await res.json();
      setEintraege(body.logs ?? []);
      setGesamt(body.total ?? 0);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das Protokoll konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [seite, proSeite, aktion, bereich, suche, zeitraum]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const kopiere = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const klappe = (id: string) => {
    setOffen((prev) => {
      const neu = new Set(prev);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  };

  const seitenZahl = Math.ceil(gesamt / proSeite);
  const eingabeKlasse =
    'rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 text-sm text-tinte';

  if (!user || !['SUPERADMIN', 'SYSTEM'].includes(user.role)) {
    return (
      <LeitstandRahmen titel="Protokoll" benutzer={user?.displayName}>
        <Notiz titel="Kein Zugriff">
          Das Protokoll enthält Alt-Werte aus allen Bereichen und ist deshalb SYSTEM und SUPERADMIN
          vorbehalten.
        </Notiz>
      </LeitstandRahmen>
    );
  }

  return (
    <LeitstandRahmen titel="Protokoll" benutzer={user?.displayName}>
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kennzahl marke="Einträge im Zeitraum" wert={gesamt.toLocaleString('de-DE')} />
          <Kennzahl
            marke="Zeitraum"
            klein
            wert={ZEITRAEUME.find((z) => z.schluessel === zeitraum)?.label.toLowerCase() ?? ''}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Umschalter
            wert={zeitraum}
            optionen={ZEITRAEUME}
            onWechsel={(z) => {
              setZeitraum(z);
              setSeite(0);
            }}
          />
          <select
            value={aktion}
            onChange={(e) => {
              setAktion(e.target.value);
              setSeite(0);
            }}
            className={eingabeKlasse}
          >
            <option value="">Alle Aktionen</option>
            {AKTIONEN.map((a) => (
              <option key={a.code} value={a.code}>
                {a.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={bereich}
            placeholder="Bereich (z. B. shifts) …"
            onChange={(e) => {
              setBereich(e.target.value);
              setSeite(0);
            }}
            className={`${eingabeKlasse} w-44`}
          />
          <input
            type="text"
            value={suchtext}
            placeholder="Suchen (Benutzer, Inhalt) …"
            onChange={(e) => setSuchtext(e.target.value)}
            className={`${eingabeKlasse} min-w-[220px] flex-1`}
          />
          {(aktion || bereich || suchtext || zeitraum !== '7d') && (
            <button
              onClick={() => {
                setAktion('');
                setBereich('');
                setSuchtext('');
                setZeitraum('7d');
                setSeite(0);
              }}
              className="font-zahl text-xs text-tinte-3 underline"
            >
              Filter zurücksetzen
            </button>
          )}
        </div>

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !eintraege.length && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Protokoll …</p>
        )}

        {!fehler && !laden && eintraege.length === 0 && (
          <Karte titel="Protokoll" marke="alle Änderungen, auditiert">
            <p className="py-6 text-center text-sm text-tinte-3">
              Keine Einträge im gewählten Zeitraum.
            </p>
          </Karte>
        )}

        {eintraege.length > 0 && (
          <Karte titel="Protokoll" marke="Zeile antippen für Einzelheiten" tabelle>
            <div className="overflow-x-auto">
              <Tabelle>
                <thead>
                  <tr>
                    <Kopfzelle>Zeitpunkt</Kopfzelle>
                    <Kopfzelle>Benutzer</Kopfzelle>
                    <Kopfzelle>Aktion</Kopfzelle>
                    <Kopfzelle>Bereich</Kopfzelle>
                    <Kopfzelle rechts>Kennung</Kopfzelle>
                  </tr>
                </thead>
                <tbody>
                  {eintraege.map((e, i) => {
                    const hatDetails = !!(e.details || e.oldValue || e.newValue);
                    const istOffen = offen.has(e.id);
                    const letzte = i === eintraege.length - 1 && !istOffen;
                    return (
                      <React.Fragment key={e.id}>
                        <tr
                          onClick={() => hatDetails && klappe(e.id)}
                          className={hatDetails ? 'cursor-pointer hover:bg-flaeche' : ''}
                        >
                          <Zelle zahl letzte={letzte}>
                            {zeitText(e.timestamp)}
                          </Zelle>
                          <Zelle letzte={letzte}>{e.user.displayName}</Zelle>
                          <Zelle letzte={letzte}>
                            <span className={e.action === 'DELETE' ? 'text-stoer' : undefined}>
                              {aktionsLabel(e.action)}
                            </span>
                          </Zelle>
                          <Zelle letzte={letzte}>{e.entity}</Zelle>
                          <Zelle rechts zahl letzte={letzte}>
                            <span className="text-tinte-3">
                              {e.entityId ? `${e.entityId.slice(0, 10)}…` : '—'}
                              {hatDetails && <span className="ml-2">{istOffen ? '▾' : '▸'}</span>}
                            </span>
                          </Zelle>
                        </tr>
                        {istOffen && hatDetails && (
                          <tr>
                            <td
                              colSpan={5}
                              className={`bg-flaeche px-3 py-4 ${i === eintraege.length - 1 ? '' : 'border-b border-linie'}`}
                            >
                              <div className="flex flex-col gap-3">
                                {e.details && (
                                  <WertBlock
                                    marke="Einzelheiten"
                                    roh={e.details}
                                    id={`d-${e.id}`}
                                    copiedId={copiedId}
                                    onCopy={kopiere}
                                  />
                                )}
                                {e.oldValue && (
                                  <WertBlock
                                    marke="Alter Wert"
                                    roh={e.oldValue}
                                    id={`a-${e.id}`}
                                    copiedId={copiedId}
                                    onCopy={kopiere}
                                  />
                                )}
                                {e.newValue && (
                                  <WertBlock
                                    marke="Neuer Wert"
                                    roh={e.newValue}
                                    id={`n-${e.id}`}
                                    copiedId={copiedId}
                                    onCopy={kopiere}
                                  />
                                )}
                                {(e.ip || e.userAgent) && (
                                  <p className="border-t border-linie pt-2 font-zahl text-[11px] text-tinte-3">
                                    {e.ip && <>IP {e.ip}</>}
                                    {e.ip && e.userAgent && ' · '}
                                    {e.userAgent && <>{e.userAgent.slice(0, 70)}…</>}
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Tabelle>
            </div>
          </Karte>
        )}

        {gesamt > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-tinte-2">
            <label className="flex items-center gap-2">
              <select
                value={proSeite}
                onChange={(e) => {
                  setProSeite(Number(e.target.value));
                  setSeite(0);
                }}
                className={eingabeKlasse}
              >
                {SEITEN_GROESSEN.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              je Seite
            </label>
            <div className="flex items-center gap-2">
              <span className="font-zahl text-xs text-tinte-3">
                {seite * proSeite + 1}–{Math.min((seite + 1) * proSeite, gesamt)} von{' '}
                {gesamt.toLocaleString('de-DE')}
              </span>
              <button
                onClick={() => setSeite((s) => Math.max(0, s - 1))}
                disabled={seite === 0}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2 disabled:opacity-40"
              >
                Zurück
              </button>
              <button
                onClick={() => setSeite((s) => s + 1)}
                disabled={seite + 1 >= seitenZahl}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2 disabled:opacity-40"
              >
                Weiter
              </button>
            </div>
          </div>
        )}
      </div>
    </LeitstandRahmen>
  );
}
